import React from 'react';
import { CustomColorPicker } from './PropertiesPanel';
import type { EditorialPropertyContext } from './editorialBlockProperties';
import { ImageSourceField } from './editorialBlockProperties';
import { isHeroCoreTemplate } from '../utils/heroContentUtils';
import { PaddingControl } from './PropertiesPanel';

const TEMPLATE_LABELS: Record<string, string> = {
  standard: 'Hero Content',
  'dark-centered': 'Centered (Dark)',
  split: 'Split Image',
  minimal: 'Minimal',
  'centered-light': 'Centered (Light)',
  'banner-image': 'Image Banner',
  'accent-line': 'Accent Line',
  'overlay-minimal': 'Overlay Minimal',
};

const IMAGE_TEMPLATES = new Set([
  'standard',
  'split',
  'banner-image',
  'overlay-minimal',
]);

const EYEBROW_TEMPLATES = new Set([
  'centered-light',
  'accent-line',
  'overlay-minimal',
]);

function HeroColorField({
  ctx,
  id,
  label,
  value,
  onChange,
}: {
  ctx: EditorialPropertyContext;
  id: string;
  label: string;
  value: string;
  onChange: (color: string) => void;
}) {
  const {
    showColorPicker,
    setShowColorPicker,
    colorPickerRef,
    getEmailBackgroundColors,
    brandColors,
    showAdvancedPicker,
    setShowAdvancedPicker,
    setAdvancedPickerPosition,
  } = ctx;

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowColorPicker(showColorPicker === id ? null : id)}
          className="w-full px-3 py-2.5 border border-slate-200 rounded-xl flex items-center gap-3 bg-white hover:border-slate-300 transition-colors"
        >
          <div className="w-8 h-8 rounded-lg border border-slate-200 shadow-inner" style={{ backgroundColor: value }} />
          <span className="text-sm text-slate-700 font-mono uppercase">{value}</span>
        </button>
        {showColorPicker === id && (
          <div ref={colorPickerRef} className="absolute z-[101] mt-1 left-0 right-0">
            <CustomColorPicker
              currentColor={value}
              onChange={onChange}
              pickerType="solid"
              emailColors={getEmailBackgroundColors()}
              brandColors={brandColors}
              showAdvancedPicker={showAdvancedPicker}
              setShowAdvancedPicker={setShowAdvancedPicker}
              setAdvancedPickerPosition={setAdvancedPickerPosition}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function HeroContentPropertiesPanel({ ctx }: { ctx: EditorialPropertyContext }) {
  const { block, handleContentChange, handleStyleChange } = ctx;
  const template = block.content.template || 'standard';
  const showImage = IMAGE_TEMPLATES.has(template);
  const showEyebrow = EYEBROW_TEMPLATES.has(template);
  const showAccent = template === 'accent-line';
  const isMinimal = template === 'minimal';
  const showButtonStyle = ['centered-light', 'accent-line', 'banner-image'].includes(template);

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Layout preset</p>
        <p className="text-sm font-medium text-slate-800">
          {TEMPLATE_LABELS[template] || template}
        </p>
      </div>

      <div className="space-y-4">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Content</h4>
        {showImage && <ImageSourceField ctx={ctx} field="image" label="Hero image" />}
        {showEyebrow && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Eyebrow label</label>
            <input
              type="text"
              value={block.content.subtitle || ''}
              onChange={(e) => handleContentChange('subtitle', e.target.value)}
              placeholder="e.g. New collection"
              className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:border-slate-400 outline-none"
            />
          </div>
        )}
        {showEyebrow && (
          <HeroColorField
            ctx={ctx}
            id="hero-eyebrow-color"
            label="Eyebrow color"
            value={block.content.eyebrowColor || block.content.textColor || '#6B7280'}
            onChange={(c) => handleContentChange('eyebrowColor', c)}
          />
        )}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Heading</label>
          <input
            type="text"
            value={block.content.heading || ''}
            onChange={(e) => handleContentChange('heading', e.target.value)}
            className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:border-slate-400 outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Body text</label>
          <textarea
            value={block.content.text || ''}
            onChange={(e) => handleContentChange('text', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:border-slate-400 outline-none"
          />
        </div>
      </div>

      {showAccent && (
        <div className="space-y-4 pt-2 border-t border-slate-200">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Accent line</h4>
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-slate-700">Show accent line</label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={block.content.showAccentLine !== false}
                onChange={(e) => handleContentChange('showAccentLine', e.target.checked)}
              />
              <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-500" />
            </label>
          </div>
          {block.content.showAccentLine !== false && (
            <>
              <HeroColorField
                ctx={ctx}
                id="hero-accent-color"
                label="Line color"
                value={block.content.accentColor || '#111827'}
                onChange={(c) => handleContentChange('accentColor', c)}
              />
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Line weight</label>
                <div className="flex gap-2 mb-2">
                  {[
                    { label: 'Thin', value: 2 },
                    { label: 'Medium', value: 4 },
                    { label: 'Bold', value: 6 },
                    { label: 'Heavy', value: 8 },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleContentChange('accentLineWidth', opt.value)}
                      className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border ${
                        Number(block.content.accentLineWidth ?? 4) === opt.value
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-600 border-slate-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={16}
                    value={block.content.accentLineWidth ?? 4}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      handleContentChange('accentLineWidth', Number.isNaN(n) ? 4 : n);
                    }}
                    className="w-20 px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:border-slate-400 outline-none"
                  />
                  <span className="text-sm text-slate-500">px</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <div className="space-y-4 pt-2 border-t border-slate-200">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Call to action</h4>
        {isMinimal && (
          <p className="text-xs text-slate-500">Optional — enable to add a button below the intro text.</p>
        )}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-slate-700">Show button</label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={block.content.showButton !== false}
                onChange={(e) => handleContentChange('showButton', e.target.checked)}
              />
              <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-500" />
            </label>
          </div>
          <PaddingControl
            label="Button padding"
            value={block.content.buttonPadding || '12px 24px'}
            onChange={(val) => handleContentChange('buttonPadding', val)}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Button text</label>
              <input
                type="text"
                value={block.content.buttonText || ''}
                onChange={(e) => handleContentChange('buttonText', e.target.value)}
                className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:border-slate-400 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Button URL</label>
              <input
                type="text"
                value={block.content.buttonUrl || ''}
                onChange={(e) => handleContentChange('buttonUrl', e.target.value)}
                className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:border-slate-400 outline-none"
              />
            </div>
          </div>
          <HeroColorField
            ctx={ctx}
            id="hero-btn-bg"
            label="Button background"
            value={block.content.buttonColor || '#3B82F6'}
            onChange={(c) => handleContentChange('buttonColor', c)}
          />
          <HeroColorField
            ctx={ctx}
            id="hero-btn-text"
            label="Button text color"
            value={block.content.buttonTextColor || '#FFFFFF'}
            onChange={(c) => handleContentChange('buttonTextColor', c)}
          />
          {showButtonStyle && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Button style</label>
              <div className="flex gap-2">
                {[
                  { label: 'Filled', value: 'filled' },
                  { label: 'Outline', value: 'outline' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleContentChange('buttonStyle', opt.value)}
                    className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border ${
                      (block.content.buttonStyle || 'filled') === opt.value
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-600 border-slate-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Button corner radius</label>
            <div className="flex gap-2 mb-2">
              {[
                { label: 'Square', value: '0' },
                { label: 'Rounded', value: '8px' },
                { label: 'Pill', value: '9999px' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleContentChange('buttonBorderRadius', opt.value)}
                  className={`flex-1 py-1.5 text-xs font-semibold rounded-lg border ${
                    (block.content.buttonBorderRadius ?? '0') === opt.value
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-600 border-slate-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={block.content.buttonBorderRadius ?? '0'}
              onChange={(e) => handleContentChange('buttonBorderRadius', e.target.value)}
              placeholder="e.g. 0, 8px, 9999px"
              className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:border-slate-400 outline-none"
            />
          </div>
      </div>

      <div className="space-y-4 pt-2 border-t border-slate-200">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Spacing</h4>
        <PaddingControl
          label="Block padding"
          value={block.styles?.padding || '0'}
          onChange={(val) => handleStyleChange('padding', val)}
        />
      </div>

      <div className="space-y-4 pt-2 border-t border-slate-200">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Colors</h4>
        <HeroColorField
          ctx={ctx}
          id="hero-block-bg"
          label="Block background"
          value={block.styles?.backgroundColor || '#FFFFFF'}
          onChange={(c) => handleStyleChange('backgroundColor', c)}
        />
        {(template === 'standard' || template === 'split' || template === 'banner-image') && (
          <HeroColorField
            ctx={ctx}
            id="hero-content-bg"
            label="Text column background"
            value={block.content.contentBackgroundColor || '#FFFFFF'}
            onChange={(c) => handleContentChange('contentBackgroundColor', c)}
          />
        )}
        {template === 'split' && (
          <HeroColorField
            ctx={ctx}
            id="hero-split-img-bg"
            label="Image column background"
            value={block.content.splitImageBackgroundColor || '#EFF6FF'}
            onChange={(c) => handleContentChange('splitImageBackgroundColor', c)}
          />
        )}
        <HeroColorField
          ctx={ctx}
          id="hero-heading-color"
          label="Heading color"
          value={block.content.headingColor || '#111827'}
          onChange={(c) => handleContentChange('headingColor', c)}
        />
        <HeroColorField
          ctx={ctx}
          id="hero-text-color"
          label="Body text color"
          value={block.content.textColor || '#6B7280'}
          onChange={(c) => handleContentChange('textColor', c)}
        />
      </div>

      {(template === 'minimal' ||
        template === 'standard' ||
        template === 'split' ||
        template === 'accent-line') && (
        <div className="space-y-2 pt-2 border-t border-slate-200">
          <label className="block text-sm font-medium text-slate-700 mb-1">Text alignment</label>
          <div className="flex gap-2">
            {(['left', 'center', 'right'] as const).map((align) => (
              <button
                key={align}
                type="button"
                onClick={() => handleStyleChange('textAlign', align)}
                className={`flex-1 py-2 text-xs font-semibold rounded-lg border capitalize ${
                  (block.styles?.textAlign || 'left') === align
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200'
                }`}
              >
                {align}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function shouldUseHeroContentPropertiesPanel(template?: string): boolean {
  return isHeroCoreTemplate(template || 'standard');
}
